import { useState, useRef } from 'react';
import { FileAudio, Loader2, Upload, Copy, Check, AlertTriangle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { ttsApi } from '../utils/api';
import { useAuthStore } from '../stores/authStore';
import { useQuotaUpdate } from '../hooks/useQuotaUpdate';

const Transcribe = () => {
  const { t } = useTranslation('transcribe');
  const { user } = useAuthStore();
  const { refreshQuota } = useQuotaUpdate();
  const noQuota = !user || user.remaining <= 0;
  const [audioFile, setAudioFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [transcribedText, setTranscribedText] = useState('');
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      setAudioFile(e.target.files[0]);
      setTranscribedText('');
      setError('');
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!audioFile) {
      setError(t('error'));
      return;
    }

    setError('');
    setLoading(true);
    setTranscribedText('');

    try {
      const formData = new FormData();
      formData.append('audio', audioFile);

      const response = await ttsApi.transcribe(formData);
      setTranscribedText(response.data.text);
      refreshQuota();
    } catch (err: any) {
      setError(err.response?.data?.detail || t('error'));
    } finally {
      setLoading(false);
    }
  };

  const handleCopy = () => {
    navigator.clipboard.writeText(transcribedText);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-8">
        <h1 className="text-2xl font-bold text-gray-900">{t('title')}</h1>
        <p className="text-gray-600 mt-1">
          {t('description')}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* 上传区域 */}
        <div>
          <form onSubmit={handleSubmit} className="card space-y-6">
            {noQuota && (
              <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg text-amber-800 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 flex-shrink-0" />
                <span>{t('error.quotaExceeded', { defaultValue: '今日配额已用完，请明天再试或升级 VIP' })}</span>
              </div>
            )}

            {error && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-red-600">
                {error}
              </div>
            )}

            <div
              onClick={() => fileInputRef.current?.click()}
              className="border-2 border-dashed border-gray-300 rounded-lg p-12 text-center cursor-pointer hover:border-primary-500 transition-colors"
            >
              <FileAudio className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg text-gray-600 mb-2">
                {audioFile ? t('upload.uploaded', { fileName: audioFile.name }) : t('upload.title')}
              </p>
              <p className="text-sm text-gray-500">
                {t('upload.supportFormats')}
              </p>
            </div>
            <input
              ref={fileInputRef}
              type="file"
              accept="audio/*"
              onChange={handleFileChange}
              className="hidden"
            />

            {audioFile && (
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-sm text-gray-600">
                  {t('upload.fileName', { fileName: audioFile.name })}
                </p>
                <p className="text-sm text-gray-600">
                  {t('upload.fileSize', { size: (audioFile.size / 1024 / 1024).toFixed(2) })}
                </p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading || !audioFile || noQuota}
              className="w-full btn-primary flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {t('loading')}
                </>
              ) : (
                <>
                  <Upload className="w-5 h-5" />
                  {t('submit')}
                </>
              )}
            </button>
          </form>
        </div>

        {/* 结果区域 */}
        <div>
          {transcribedText ? (
            <div className="card">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900">{t('result.title')}</h3>
                <button
                  onClick={handleCopy}
                  className="flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
                >
                  {copied ? (
                    <>
                      <Check className="w-4 h-4" />
                      {t('result.copied')}
                    </>
                  ) : (
                    <>
                      <Copy className="w-4 h-4" />
                      {t('result.copy')}
                    </>
                  )}
                </button>
              </div>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="text-gray-800 whitespace-pre-wrap">
                  {transcribedText}
                </p>
              </div>
              <p className="text-xs text-gray-500 mt-4">
                {t('result.hint')}
              </p>
            </div>
          ) : (
            <div className="card h-full flex items-center justify-center text-gray-400">
              <div className="text-center">
                <FileAudio className="w-16 h-16 mx-auto mb-4" />
                <p>{t('empty.title')}</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Transcribe;
